/**
 * DifferenceSection Organism Component
 * "How We Make The Difference" - 2-column section with feature blocks and graphic
 */

import React, { useState, useEffect } from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Image } from "@/components/atoms/Image";
import { Icon } from "@/components/atoms/Icon";
import { DifferenceFeatureBlock } from "@/components/molecules/DifferenceFeatureBlock";
import type { DifferenceSectionBlok } from "@/types";

export interface DifferenceSectionProps {
  blok: DifferenceSectionBlok;
}

export const DifferenceSection: React.FC<DifferenceSectionProps> = ({
  blok,
}) => {
  const {
    section_title,
    section_icon,
    feature_blocks,
    graphic,
    graphic_light,
  } = blok;

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const graphicSrc = isDark
    ? graphic?.filename
    : graphic_light?.filename || graphic?.filename;

  return (
    <section
      {...storyblokEditable(blok)}
      className={`bg-theme-surface py-12 md:py-16 lg:py-20 xl:py-24`}
    >
      <Container>
        {/* Section Header */}
        <div className="flex flex-col items-start gap-4 md:gap-6 mb-4 md:mb-6">
          <div className="p-2 md:p-3 bg-theme-inset rounded-lg">
            <Icon
              name="difference"
              size={36}
              className="text-black dark:text-primary"
            />
          </div>
          <Heading level="h2" className="text-black dark:text-primary">
            {section_title}
          </Heading>
        </div>

        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 lg:gap-16 items-center">
          {/* Left Column: Feature Blocks */}
          <div className="mt-6 md:mt-12 flex flex-col gap-6 md:gap-10 lg:gap-12">
            {feature_blocks &&
              feature_blocks.map((feature) => (
                <DifferenceFeatureBlock
                  key={feature._uid}
                  icon={feature.icon}
                  icon_light={feature.icon_light}
                  title={feature.title}
                  description={feature.description}
                  features={feature.features}
                />
              ))}
          </div>

          {/* Right Column: Circular Chart Graphic */}
          {graphic && (
            <div className="flex items-center justify-center">
              <div className="relative w-full lg:max-w-md">
                <Image
                  src={graphicSrc || graphic.filename}
                  alt={graphic.alt || "How we make the difference"}
                  className="w-full h-auto"
                />
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default DifferenceSection;
